/**
 * Bounded Context: Privacy Disclosure (shared)
 *
 * Composable que descreve o estado de privacidade local-first do HLOS.
 * É puramente informativo (CC-09 Explicabilidade + CL-08). Não muta
 * estado — apenas lê.
 *
 * Constitucional:
 * - CL-07 (local-first): isLocal é sempre true em v0.4 (vault plugado em HLOS-7).
 * - CL-08 (ética fechada): zero key escrow, zero sync plaintext.
 * - Princípio XI: explicita o que o LLM nunca vê (θ_lat, memória, nome, goals).
 * - CC-09: explicabilidade via DataFlow auditável.
 * - LGPD: privacy by design.
 */

import { computed, readonly as readonlyRef, ref, type Ref } from "vue";

/**
 * Tipos de dados que fluem no HLOS Companion.
 * Cada tipo é categorizado em `dataFlows` para disclosure ao usuário.
 */
export type DataKind =
  | "θ_lat" // Estado latente do aprendiz (privado).
  | "memory" // Memória de longo prazo.
  | "name" // Nome/apelido do aprendiz.
  | "goals" // Objetivos declarados.
  | "conversation" // Turnos do learner/companion (podem ser sensíveis).
  | "ka_topic" // Tópico de atom de conhecimento (não-sensível).
  | "rt_signal"; // Tempo de reação (pseudônimo).

/** Visibilidade de um DataKind: o que pode ser enviado para o LLM. */
export type Visibility = "local-only" | "opt-in" | "public";

/** Item de disclosure: o que o HLOS faz com cada tipo de dado. */
export interface DataFlow {
  readonly kind: DataKind;
  readonly visibility: Visibility;
  /** Explicação legível (PT-BR). */
  readonly description: string;
}

/** Lista canônica de DataFlows auditáveis. */
export const DATA_FLOWS: readonly DataFlow[] = Object.freeze([
  {
    kind: "θ_lat",
    visibility: "local-only",
    description: "Estado latente — nunca sai do dispositivo.",
  },
  {
    kind: "memory",
    visibility: "local-only",
    description: "Memória de longo prazo — armazenada no vault local.",
  },
  {
    kind: "name",
    visibility: "opt-in",
    description: "Nome — você pode escolher se sincroniza (opt-in explícito).",
  },
  {
    kind: "goals",
    visibility: "opt-in",
    description: "Objetivos — você pode escolher se sincroniza.",
  },
  {
    kind: "conversation",
    visibility: "opt-in",
    description: "Turnos da conversa — você pode escolher se envia ao LLM.",
  },
  {
    kind: "ka_topic",
    visibility: "public",
    description: "Tópico do atom — não-sensível, pode aparecer em logs.",
  },
  {
    kind: "rt_signal",
    visibility: "public",
    description: "Tempo de reação — pseudonimizado, não identifica você.",
  },
]);

/** Estado de privacy disclosure retornado pelo composable. */
export interface PrivacyDisclosure {
  /** True se tudo é processado localmente (sem rede para dados sensíveis). */
  readonly isLocal: true;
  /** Resumo legível do estado. */
  readonly summary: string;
  /** Lista de DataFlows (auditável). */
  readonly dataFlows: readonly DataFlow[];
  /** Texto literal para UI (CL-09 explicabilidade). */
  readonly trustLine: string;
}

/**
 * Composable que retorna o estado de privacy disclosure.
 * Estável (sempre os mesmos valores em v0.4) — futuro: reativo a vault/online.
 */
export function usePrivacyDisclosure() {
  const trustLine = ref<string>(
    "Tudo local · LLM nunca vê θ_lat, memória, nome ou goals",
  );

  const isLocal = ref<true>(true);

  const dataFlows = ref<readonly DataFlow[]>(DATA_FLOWS);

  const summary = computed<string>(() => {
    const localCount = DATA_FLOWS.filter((d) => d.visibility === "local-only").length;
    const optInCount = DATA_FLOWS.filter((d) => d.visibility === "opt-in").length;
    return `${localCount} tipos locais · ${optInCount} opt-in · zero key escrow`;
  });

  const view = (): PrivacyDisclosure => ({
    isLocal: isLocal.value,
    summary: summary.value,
    dataFlows: dataFlows.value,
    trustLine: trustLine.value,
  });

  return {
    isLocal: readonlyRef(isLocal) as unknown as Ref<true>,
    trustLine: readonlyRef(trustLine),
    dataFlows: readonlyRef(dataFlows),
    summary: readonlyRef(summary),
    view,
  };
}
